'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import AnimatedButton from '@/components/shared/AnimatedButton';
import Input from '@/components/shared/Fields/input';
import CustomDropdown from '@/components/shared/Fields/select';
import Honeypot, { HoneypotHandle } from '@/components/shared/Honeypot';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import { buildLeadFormDefaults, buildLeadSchema, getLeadFieldDefinition, LeadFormData } from '@/constants/lead-magnet-form';
import { LeadForm as LeadFormModel } from '@/types/lead-magnet';
import css from './style.module.scss';
interface Props {
  form: LeadFormModel;
  isSubmitting: boolean;
  onSubmit: (data: LeadFormData) => void | Promise<void>;
}
const LeadMagnetLeadForm: FC<Props> = ({
  form,
  isSubmitting,
  onSubmit
}) => {
  const honeypotRef = useRef<HoneypotHandle>(null);
  const schema = useMemo(() => buildLeadSchema(form.fields), [form.fields]);
  const defaultValues = useMemo(() => buildLeadFormDefaults(form.fields), [form.fields]);
  const {
    control,
    handleSubmit,
    formState: {
      errors
    }
  } = useForm<LeadFormData>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues
  });
  if (!form.fields.length) return null;
  const submit = handleSubmit(data => {
    if (honeypotRef.current?.isFilled()) return;
    return onSubmit(data);
  });
  return <form noValidate className={css.form} onSubmit={submit}>
			<Honeypot ref={honeypotRef} />

			<div className={css.fields}>
				{form.fields.map(field => {
        const definition = getLeadFieldDefinition(field.name);
        if (!definition) return null;
        const placeholder = field.placeholder ?? field.label;
        const error = errors[field.name]?.message;
        return <div key={field.name} className={css.field}>
							<Controller name={field.name} control={control} render={({
            field: control_0
          }) => definition.type === 'select' ? <CustomDropdown name={control_0.name} placeholder={placeholder} options={field.options} value={field.options.find(option => option.value === control_0.value) ?? null} onChange={control_0.onChange} /> : <Input {...control_0} type={definition.type} placeholder={placeholder} allowedPattern={definition.allowedPattern} />} />

							{error ? <Typography variant="body-s" tag="p" className={css.error}>
									{error}
								</Typography> : null}
						</div>;
      })}
			</div>

			<div className={css.footer}>
				{form.privacyText ? <Typography variant="body-s" tag="div" className={css.privacy}>
						<StrapiRichContent content={form.privacyText} />
					</Typography> : <span />}

				<AnimatedButton buttonType="submit" wrapperClassName={css.submitWrapper} className={css.submit} classNameText={css.submitLabel} disabled={isSubmitting}>
					{form.submitLabel}
				</AnimatedButton>
			</div>
		</form>;
};
export default LeadMagnetLeadForm;